import ViewBtn from "@/components/Buttons/ViewBtn";
import CommonDelete from "@/components/CommonUi/CommonDelete";
import CreateDrawer from "@/components/CommonUi/CreateDrawer";
import TableComponent from "@/components/CommonUi/TableComponent";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import {
  deleteAgent,
  loadAllAgentPaginated,
  changeAgentStatus,
} from "@/redux/rtk/features/CRM/agent/agentSlice";
import { StopOutlined, CheckCircleOutlined } from "@ant-design/icons";
import Modal from "@/UI/Modal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CiEdit } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AddCompany from "@/components/CRM/Company/AddCompany";
import UpdateCompany from "@/components/CRM/Company/UpdateCompany";
import useCompanyFilter from "@/components/CRM/Company/useCompanyFilter";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";

export default function GetAllAgents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, total } = useSelector((state) => state.agent);
  const [pageConfig, setPageConfig] = useState({ page: 1, count: 10 });
  const [edit, setEdit] = useState();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useCompanyFilter(filtersOpen);

  const columns = [
    {
      title: t("common.name"),
      key: "companyName",
      dataIndex: "companyName",
      render: (companyName, { id }) => (
        <Link to={`/admin/agent/${id}`}>{companyName}</Link>
      ),
      renderCsv: (companyName) => companyName,
    },
    {
      title: t("common.email"),
      dataIndex: "email",
      key: "email",
      renderCsv: (email) => email,
    },
    {
      title: t("common.phone"),
      dataIndex: "phone",
      key: "phone",
      renderCsv: (phone) => phone,
    },
    {
      title: t("company.owner"),
      dataIndex: "companyOwner",
      key: "owner",
      render: (companyOwner, item) => (
        <Link to={`/admin/staff/${item?.companyOwnerId}`}>
          {companyOwner?.firstName} {companyOwner?.lastName}
        </Link>
      ),
      renderCsv: (companyOwner) =>
        companyOwner ? `${companyOwner.firstName} ${companyOwner.lastName}` : "-",
    },
    {
      title: t("company.studentsNumber"),
      dataIndex: "studentsNumber",
      key: "studentsNumber",
      renderCsv: (num) => num,
    },
    {
      title: t("navigation.industry"),
      dataIndex: "industry",
      key: "industry",
      render: (industry) => industry?.industryName,
      renderCsv: (industry) => industry?.industryName,
    },
    {
      title: t("common.createdAt"),
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleString() : "-"),
      renderCsv: (date) => (date ? new Date(date).toLocaleString() : "-"),
    },
    {
      title: t("common.status") || "Status",
      dataIndex: "isActive",
      key: "status",
      render: (isActive) => {
        const active = isActive !== "false";
        return (
          <span className={`px-2 py-1 rounded-full text-xs ${active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {active ? t("common.active") || "Active" : t("common.inactive") || "Inactive"}
          </span>
        );
      },
      renderCsv: (isActive) => (isActive !== "false" ? "Active" : "Inactive"),
    },
    {
      title: "",
      key: "action",
      render: (item) => {
        const active = item?.isActive !== "false";
        return [
          {
            label: <ViewBtn title={t("common.view")} path={`/admin/agent/${item?.id}`} />,
            key: "view",
          },
          {
            label: (
              <span className="cursor-pointer" onClick={() => setEdit(item)}>
                <button className="flex justify-center items-center gap-2 rounded text-blue-600 hover:text-blue-800">
                  <CiEdit size={18} /> {t("common.edit")}
                </button>
              </span>
            ),
            key: "edit",
          },
          {
            label: (
              <CommonDelete
                values={{ id: item?.id, isActive: active ? "false" : "true" }}
                title={active ? t("common.deactivate") || "Deactivate" : t("common.activate") || "Activate"}
                confirmMessage={t("common.areYouSure", { action: active ? t("common.deactivate") || "Deactivate" : t("common.activate") || "Activate" })}
                permission={"status-agent"}
                deleteThunk={changeAgentStatus}
                loadThunk={loadAllAgentPaginated}
                query={pageConfig}
                className="bg-white text-black"
                icon={active ? <StopOutlined className="text-red-500" /> : <CheckCircleOutlined className="text-green-500" />}
              />
            ),
            key: "status",
          },
          {
            label: (
              <CommonDelete
                id={item?.id}
                title={t("common.delete")}
                permission={"delete-agent"}
                deleteThunk={deleteAgent}
                loadThunk={loadAllAgentPaginated}
                query={pageConfig}
                className="bg-white text-black"
              />
            ),
            key: "delete",
          },
        ];
      },
      csvOff: true,
    },
  ];

  useEffect(() => {
    dispatch(loadAllAgentPaginated(pageConfig));
  }, [dispatch, pageConfig]);

  return (
    <UserPrivateComponent permission={["readAll-agent", "readSingle-agent"]}>
      <TableComponent
        list={list}
        columns={columns}
        loading={loading}
        total={total}
        setPageConfig={setPageConfig}
        onRow={(item) => ({
          onClick: () => navigate(`/admin/agent/${item?.id}`),
          className: "cursor-pointer",
        })}
        title={t("navigation.agent") + " " + t("common.list")}
        isSearch
        filters={filters}
        componentTitle={t("navigation.agent")}
        extraFilter={
          <AdvancedFilter
            page="agent"
            filters={filters}
            pageConfig={pageConfig}
            setPageConfig={setPageConfig}
            onOpen={() => setFiltersOpen(true)}
          />
        }
        extra={
          <CreateDrawer
            permission={"create-agent"}
            title={t("navigation.agent")}>
            <AddCompany />
          </CreateDrawer>
        }
      />

      <Modal
        open={edit}
        onClose={() => setEdit(false)}
        className="md:w-[70%] px-5"
        title={t("common.edit") + " " + t("navigation.agent")}>
        <UpdateCompany data={edit} />
      </Modal>
    </UserPrivateComponent>
  );
}
